'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { ChevronDown } from 'lucide-react';
import {
  featuresGroup,
  primaryLinks,
  solutionsColumns,
  solutionsGroup,
} from '@/content/navigation';
import type { NavLink } from '@/content/navigation';
import { useSignupCtas } from '@/components/utils/SignupModeProvider';
import { useCtaLinkProps } from '@/components/utils/useCtaLinkProps';
import { cn } from '@/lib/twUtils';

/**
 * Quanto aspettiamo, dopo che Radix ha deciso di chiudere, prima di chiudere
 * davvero. Radix ha già 150 ms suoi e non li rende configurabili: in tutto fa
 * poco meno di mezzo secondo, il tempo di rientrare se il mouse è scappato
 * oltre il bordo del pannello senza volerlo.
 */
const CLOSE_GRACE_MS = 260;

/** Valori dei due mega-menu: espliciti, così sappiamo quale è aperto. */
const FEATURES_VALUE = 'funzionalita';
const SOLUTIONS_VALUE = 'dedicato-a';

const triggerClasses =
  'group flex items-center gap-1.5 rounded-md px-3 py-[1.375rem] text-[0.9375rem] font-medium ' +
  'text-ink transition-colors hover:text-accent ds-focus cursor-pointer';

const linkClasses =
  'rounded-md px-3 py-[1.375rem] text-[0.9375rem] font-medium text-ink transition-colors hover:text-accent ds-focus';

const panelClasses =
  'absolute top-full left-0 w-[35rem] overflow-hidden rounded-2xl border border-edge bg-white ' +
  'p-3.5 shadow-[0_24px_60px_rgb(0_25_55/0.16)] z-50';

/** Voce del menu a tendina: titolo + descrizione. */
const MenuItem = ({ item }: { item: NavLink }) => (
  <li>
    <NavigationMenu.Link asChild>
      <Link
        href={item.href}
        className="block rounded-ds-sm px-3.5 py-3 transition-colors hover:bg-mist-soft ds-focus"
      >
        <span className="block text-[0.90625rem] font-semibold text-primary">{item.label}</span>
        {item.description && (
          <span className="mt-0.5 block text-[0.78125rem] text-ink-muted">{item.description}</span>
        )}
      </Link>
    </NavigationMenu.Link>
  </li>
);

/** Fascia in fondo al pannello: una nota a sinistra, un link a destra. */
const PanelFooter = ({
  note,
  action,
  onActionClick,
}: {
  note: string;
  action: NavLink;
  onActionClick?: (event: React.MouseEvent) => void;
}) => (
  <div className="-mx-3.5 -mb-3.5 mt-3 flex items-center justify-between gap-4 border-t border-edge-soft bg-mist px-7 py-3">
    <span className="text-[0.75rem] text-ink-muted">{note}</span>
    <NavigationMenu.Link asChild>
      <Link
        href={action.href}
        onClick={onActionClick}
        className="shrink-0 rounded-sm text-[0.8125rem] font-semibold text-accent hover:text-accent-600 ds-focus"
      >
        {action.label} →
      </Link>
    </NavigationMenu.Link>
  </div>
);

const Chevron = () => (
  <ChevronDown
    className="h-3.5 w-3.5 opacity-60 transition-transform duration-200 group-data-[state=open]:rotate-180"
    aria-hidden="true"
  />
);

/**
 * Nav desktop con due mega-menu ad apertura al passaggio del mouse.
 *
 * Il valore aperto lo teniamo noi invece di lasciarlo a Radix, perché due
 * comportamenti suoi remano contro chi usa il mouse senza precisione:
 *
 * 1. il click su una voce già aperta la chiude (`onItemSelect` fa un toggle).
 *    Chi ci passa sopra e poi ci clicca — gesto naturale — se la vede sparire.
 *    Il click del mouse non chiude più; con la tastiera Invio continua a fare
 *    da interruttore, che lì è quello che ci si aspetta.
 * 2. la chiusura scatta 150 ms dopo che il puntatore è uscito, e quel numero è
 *    scritto nella libreria. Troppo poco per uscire di un pelo dal pannello e
 *    rientrare: qui la chiusura è solo rimandata, e rientrare la annulla.
 *
 * Le chiusure volute restano istantanee: Esc, un link scelto, un click fuori.
 */
const DesktopNav = () => {
  const { primary } = useSignupCtas();
  const ctaProps = useCtaLinkProps('mega-menu');
  const navRef = useRef<HTMLElement>(null);
  const closeTimer = useRef<number | null>(null);
  const [openValue, setOpenValue] = useState('');

  const cancelClose = useCallback(() => {
    if (closeTimer.current === null) return;
    window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);

  const closeNow = useCallback(() => {
    cancelClose();
    setOpenValue('');
  }, [cancelClose]);

  const handleValueChange = useCallback(
    (next: string) => {
      cancelClose();
      if (next !== '') {
        setOpenValue(next);
        return;
      }
      closeTimer.current = window.setTimeout(() => {
        closeTimer.current = null;
        setOpenValue('');
      }, CLOSE_GRACE_MS);
    },
    [cancelClose]
  );

  // Rientrare su una voce già aperta non passa da `onValueChange` — per Radix
  // il valore non è cambiato — quindi la chiusura in sospeso va annullata qui.
  const keepOpen = useCallback(() => cancelClose(), [cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);

  // Un click fuori dal menu è una chiusura voluta: niente attesa.
  useEffect(() => {
    if (openValue === '') return;
    const handlePointerDown = (event: PointerEvent) => {
      if (navRef.current?.contains(event.target as Node)) return;
      closeNow();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [openValue, closeNow]);

  /**
   * Click su una voce già aperta. Col mouse non succede niente: chi ci passa
   * sopra e poi ci clicca non deve vedersela sparire. Da tastiera (`detail` a
   * 0 vuol dire Invio o Spazio) resta un interruttore, e lì la chiusura è
   * voluta: immediata, senza il ritardo pensato per il puntatore.
   */
  const holdOpenOnClick = (value: string) => (event: React.MouseEvent) => {
    if (openValue !== value) return;
    event.preventDefault();
    if (event.detail === 0) closeNow();
  };

  /** Scelto un link nel pannello, il pannello se ne va subito. */
  const closeOnLinkClick = (event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest('a')) closeNow();
  };

  return (
    <NavigationMenu.Root
      ref={navRef}
      value={openValue}
      onValueChange={handleValueChange}
      delayDuration={100}
      skipDelayDuration={300}
      onKeyDown={event => {
        if (event.key === 'Escape') closeNow();
      }}
      className="relative"
    >
      <NavigationMenu.List className="flex items-center gap-1">
        {/* Funzionalità */}
        <NavigationMenu.Item value={FEATURES_VALUE} className="relative">
          <NavigationMenu.Trigger
            className={triggerClasses}
            onClick={holdOpenOnClick(FEATURES_VALUE)}
            onPointerEnter={keepOpen}
          >
            {featuresGroup.label}
            <Chevron />
          </NavigationMenu.Trigger>
          <NavigationMenu.Content
            className={panelClasses}
            onPointerEnter={keepOpen}
            onClick={closeOnLinkClick}
          >
            <ul className="grid grid-cols-2 gap-1">
              {featuresGroup.items.map(item => (
                <MenuItem key={item.href} item={item} />
              ))}
            </ul>
            <PanelFooter
              note="Normativa, Giurisprudenza e Prassi sincronizzate ogni giorno"
              action={featuresGroup.indexLink}
            />
          </NavigationMenu.Content>
        </NavigationMenu.Item>

        {/* Dedicato a */}
        <NavigationMenu.Item value={SOLUTIONS_VALUE} className="relative">
          <NavigationMenu.Trigger
            className={triggerClasses}
            onClick={holdOpenOnClick(SOLUTIONS_VALUE)}
            onPointerEnter={keepOpen}
          >
            {solutionsGroup.label}
            <Chevron />
          </NavigationMenu.Trigger>
          <NavigationMenu.Content
            className={cn(panelClasses, 'left-[-1rem]')}
            onPointerEnter={keepOpen}
            onClick={closeOnLinkClick}
          >
            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
              {solutionsColumns.map(column => (
                <div key={column.title}>
                  <div className="px-3.5 pt-1.5 pb-0.5 text-[0.65625rem] font-semibold tracking-[0.12em] text-ink-faint uppercase">
                    {column.title}
                  </div>
                  <ul className="flex flex-col gap-0.5">
                    {column.items.map(item => (
                      <MenuItem key={item.href} item={item} />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <PanelFooter
              note="Pensato per il lavoro di studio, non per la chat"
              action={primary}
              onActionClick={ctaProps(primary).onClick}
            />
          </NavigationMenu.Content>
        </NavigationMenu.Item>

        {/* Fonti, Prezzi */}
        {primaryLinks.map(link => (
          <NavigationMenu.Item key={link.href}>
            <NavigationMenu.Link asChild>
              <Link href={link.href} className={linkClasses}>
                {link.label}
              </Link>
            </NavigationMenu.Link>
          </NavigationMenu.Item>
        ))}
      </NavigationMenu.List>
    </NavigationMenu.Root>
  );
};

export default DesktopNav;
